import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  reqNoticeListByNine,
  reqPlusViewCount,
  reqViewNotice,
} from "../../../requests/user";

interface NoticeItem {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  view: number;
  imageUrl?: string;
  category: string;
  subTitle?: string;
  like: number;
  isPublic?: boolean;
  updatedAt?: string;
}

interface NoticeResponse {
  code: number;
  noticeCount: number;
  noticeList: NoticeItem[];
}

interface NoticeDetailResponse {
  code: number;
  notice: NoticeItem;
}

const LNotice = () => {
  const navigate = useNavigate();
  const { noticeId } = useParams<{ noticeId: string }>();
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<NoticeItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [displayedNotices, setDisplayedNotices] = useState<NoticeItem[]>([]);
  const noticesPerPage = 9;

  const fetchNotices = async (page = currentPage, shouldReplace = false) => {
    try {
      setIsLoading(true);
      const response = await reqNoticeListByNine(page);

      if (response?.data?.code === 200) {
        const data = response.data as NoticeResponse;
        // 첫 페이지이거나 교체 플래그가 true일 경우 기존 데이터 교체
        if (page === 1 || shouldReplace) {
          setNotices(data.noticeList);
        } else {
          setNotices((prev) => [...prev, ...data.noticeList]);
        }
        setTotalCount(data.noticeCount);
      }
    } catch (error) {
      console.error("공지사항을 불러오는 데 실패했습니다:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNoticeDetail = async (id: number) => {
    try {
      setIsLoading(true);

      // 조회수 증가
      await reqPlusViewCount({ id });

      // 공지사항 상세 정보 가져오기
      const response = await reqViewNotice(id);

      if (response?.data?.code === 200) {
        // 응답 구조에 따라 notice 필드에서 데이터 가져오기
        const noticeData = response.data.notice || response.data.data;

        if (noticeData) {
          setSelectedNotice(noticeData);
        } else {
          console.error("공지사항 데이터가 notice 또는 data 필드에 없습니다.");
          navigate("/notice");
        }
      }
    } catch (error) {
      console.error("공지사항 상세 정보를 불러오는 데 실패했습니다:", error);
      navigate("/notice");
    } finally {
      setIsLoading(false);
    }
  };

  // 표시할 공지사항 업데이트
  useEffect(() => {
    // 현재 페이지에 따라 보여줄 공지사항 계산
    const endIndex = currentPage * noticesPerPage;
    setDisplayedNotices(notices.slice(0, endIndex));
  }, [notices, currentPage]);

  // URL 파라미터에 따라 공지사항 상세 또는 목록 표시
  useEffect(() => {
    // noticeId가 변경되었을 때만 실행
    if (noticeId) {
      fetchNoticeDetail(Number(noticeId));
    } else {
      // 상세 페이지에서 목록으로 돌아올 때 현재 페이지를 1로 리셋하고 데이터 다시 불러오기
      setCurrentPage(1);
      // useEffect의 의존성 배열에 currentPage가 없어서 이 위치에서 직접 호출해야 함
      const loadInitialData = async () => {
        try {
          setIsLoading(true);
          const response = await reqNoticeListByNine(1); // 항상 첫 페이지부터 로드

          if (response?.data?.code === 200) {
            const data = response.data as NoticeResponse;
            setNotices(data.noticeList); // 무조건 초기 데이터로 설정
            setTotalCount(data.noticeCount);
          }
        } catch (error) {
          console.error("공지사항을 불러오는 데 실패했습니다:", error);
        } finally {
          setIsLoading(false);
        }
      };

      loadInitialData();
    }
  }, [noticeId]);

  // 추가 페이지 로드 시 공지사항 목록만 불러오기
  useEffect(() => {
    if (!noticeId && currentPage > 1) {
      fetchNotices(currentPage, false); // 추가 페이지는 데이터 추가
    }
  }, [currentPage, noticeId]);

  const handleNoticeClick = (notice: NoticeItem) => {
    navigate(`/notice/${notice.id}`);
  };

  const handleBackToList = () => {
    navigate("/notice");
    setSelectedNotice(null);
  };

  const handleGoBack = () => {
    navigate(-1); // 브라우저 히스토리에서 이전 페이지로 이동
  };

  const loadMoreNotices = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const getImageUrl = (imageUrl?: string) => {
    if (!imageUrl) return null;

    // 이미지 URL이 절대 경로인지 확인
    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }

    // ncloud 스토리지 URL 사용
    return `https://kr.object.ncloudstorage.com/ontact-bucket/${imageUrl}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatDateDetailed = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  };

  return (
    <NoticeContainer>
      {noticeId && selectedNotice ? (
        // 공지사항 상세 페이지
        <NoticeDetailPage>
          <DetailHeader>
            <DetailCategory>{selectedNotice.category}</DetailCategory>
            <DetailTitle>{selectedNotice.title}</DetailTitle>
            {selectedNotice.subTitle && (
              <DetailSubtitle>{selectedNotice.subTitle}</DetailSubtitle>
            )}
            <DetailMeta>
              <DetailStats>
                <StatItem>
                  <HeartIcon />
                  {selectedNotice.like}
                </StatItem>
                <StatItem>
                  <EyeIcon />
                  {selectedNotice.view}
                </StatItem>
              </DetailStats>
              <DetailDate>
                {formatDateDetailed(
                  selectedNotice.updatedAt || selectedNotice.createdAt
                )}
              </DetailDate>
            </DetailMeta>
          </DetailHeader>

          {selectedNotice.imageUrl && (
            <DetailImageSection>
              <DetailImage
                src={
                  getImageUrl(selectedNotice.imageUrl) ||
                  "/placeholder-image.jpg"
                }
                alt={selectedNotice.title}
                loading="eager"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder-image.jpg";
                }}
              />
            </DetailImageSection>
          )}

          <DetailContent
            dangerouslySetInnerHTML={{ __html: selectedNotice.content }}
          />

          <NavigationBar>
            <NavigationButtons>
              <BackButton onClick={handleGoBack}>
                <ArrowIcon />
                <span className="short-text">Back</span>
                <span className="full-text">Previous page</span>
              </BackButton>
              <BackButton onClick={handleBackToList}>
                <ListIcon /> Back to list
              </BackButton>
            </NavigationButtons>
          </NavigationBar>
        </NoticeDetailPage>
      ) : (
        // 공지사항 목록 페이지
        <>
          <NoticeHeader>
            <h2>Notice</h2>
            <p>ONTACT's latest news and events</p>
          </NoticeHeader>

          <InstagramGrid>
            {displayedNotices.map((notice, index) => (
              <InstagramCard
                key={notice.id}
                onClick={() => handleNoticeClick(notice)}
              >
                <CardImageWrapper>
                  {notice.imageUrl ? (
                    <CardImage
                      src={
                        getImageUrl(notice.imageUrl) || "/placeholder-image.jpg"
                      }
                      alt={notice.title}
                      loading={index < 3 ? "eager" : "lazy"}
                      onError={(e) => {
                        // 이미지 로드 실패 시 기본 이미지로 대체
                        (e.target as HTMLImageElement).src =
                          "/placeholder-image.jpg";
                      }}
                    />
                  ) : (
                    <CardImagePlaceholder>
                      <CategoryTag>{notice.category}</CategoryTag>
                    </CardImagePlaceholder>
                  )}
                  {notice.imageUrl && (
                    <CategoryTag>{notice.category}</CategoryTag>
                  )}
                </CardImageWrapper>
                <CardContent>
                  <CardHeader>
                    <CardTitle>{notice.title}</CardTitle>
                    {notice.subTitle && (
                      <CardSubtitle>{notice.subTitle}</CardSubtitle>
                    )}
                  </CardHeader>
                  <CardFooter>
                    <CardStats>
                      <StatItem>
                        <HeartIcon />
                        {notice.like}
                      </StatItem>
                      <StatItem>
                        <EyeIcon />
                        {notice.view}
                      </StatItem>
                    </CardStats>
                    <CardDate>{formatDate(notice.createdAt)}</CardDate>
                  </CardFooter>
                </CardContent>
              </InstagramCard>
            ))}
          </InstagramGrid>

          {displayedNotices.length > 0 &&
            !isLoading &&
            displayedNotices.length < totalCount && (
              <LoadMoreButton onClick={loadMoreNotices}>
                Load More
              </LoadMoreButton>
            )}

          {isLoading && <LoadingSpinner>Loading...</LoadingSpinner>}
        </>
      )}
    </NoticeContainer>
  );
};

const NoticeContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  background-color: #fafafa;

  @media (max-width: 576px) {
    padding: 1rem;
  }
`;

const NoticeHeader = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  h2 {
    font-size: 2rem;
    color: #262626;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  p {
    color: #8e8e8e;
    font-size: 1rem;
  }
`;

// 공지사항 상세 페이지 스타일
const NoticeDetailPage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 935px;
  margin: 0 auto;
  background-color: white;
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  padding-bottom: calc(1.5rem + 70px); /* NavigationBar 높이 + 여유 공간 */

  @media (max-width: 576px) {
    padding: 1rem;
    padding-bottom: calc(1rem + 70px); /* NavigationBar 높이 + 여유 공간 */
  }
`;

const DetailHeader = styled.div`
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #efefef;
  padding-bottom: 1rem;
`;

const DetailCategory = styled.div`
  display: inline-block;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
  color: #8e8e8e;
  background-color: #fafafa;
  padding: 0.4rem 0.8rem;
  border-radius: 3px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const DetailTitle = styled.h1`
  margin: 0 0 0.75rem;
  font-size: 1.75rem;
  font-weight: 600;
  color: #262626;
  line-height: 1.3;
`;

const DetailSubtitle = styled.h2`
  margin: 0 0 1.25rem;
  font-size: 1.25rem;
  font-weight: 400;
  color: #8e8e8e;
  line-height: 1.4;
`;

const DetailMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const DetailStats = styled.div`
  display: flex;
  gap: 16px;
`;

const DetailDate = styled.div`
  font-size: 0.85rem;
  color: #8e8e8e;
`;

const DetailImageSection = styled.div`
  width: 100%;
  margin: 0 auto 1.5rem;
  text-align: center;
  border-radius: 3px;
  overflow: hidden;
`;

const DetailImage = styled.img`
  max-width: 100%;
  height: auto;
`;

const DetailContent = styled.div`
  line-height: 1.8;
  font-size: 1rem;
  color: #262626;
  padding-bottom: 1.5rem;

  p {
    margin-bottom: 1.5rem;
  }

  a {
    color: #0095f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    margin: 1.5rem 0;
    border-radius: 3px;
  }

  ul,
  ol {
    margin: 1.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.8rem;
  }

  strong,
  b {
    font-weight: 600;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 2rem 0 1rem;
    font-weight: 600;
    color: #262626;
  }

  blockquote {
    margin: 1.5rem 0;
    padding: 1rem 1.5rem;
    border-left: 3px solid #efefef;
    color: #8e8e8e;
    font-style: italic;
  }
`;

const InstagramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const InstagramCard = styled.div`
  border-radius: 3px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CardImageWrapper = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  background-color: #fafafa;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CategoryTag = styled.div<{ large?: boolean }>`
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: ${(props) => (props.large ? "8px 16px" : "4px 8px")};
  border-radius: 3px;
  font-size: ${(props) => (props.large ? "0.9rem" : "0.7rem")};
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const CardContent = styled.div`
  padding: 12px;
`;

const CardHeader = styled.div`
  margin-bottom: 8px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #262626;
`;

const CardSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #8e8e8e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #efefef;
`;

const CardStats = styled.div`
  display: flex;
  gap: 12px;
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #8e8e8e;
  font-size: 0.8rem;

  i {
    font-size: 0.9rem;
  }
`;

const CardDate = styled.span`
  font-size: 0.75rem;
  color: #8e8e8e;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 2.5rem auto 0;
  padding: 0.8rem 2rem;
  background-color: white;
  color: #262626;
  border: 1px solid #dbdbdb;
  border-radius: 3px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #fafafa;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  margin: 2rem 0;
  color: #8e8e8e;
  font-size: 0.9rem;
`;

// 아이콘 컴포넌트 추가
const HeartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const NavigationBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  padding: 1.25rem;
  margin-top: 0;
  background-color: white;
  border-top: 1px solid #efefef;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 100;
  transition: transform 0.3s ease;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 935px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 576px) {
    gap: 0.5rem;
    justify-content: space-between;
    width: 100%;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #262626;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  border-radius: 3px;
  transition: all 0.2s ease;
  background-color: white;
  border: 1px solid #dbdbdb;

  &:hover {
    background-color: #fafafa;
  }

  &:active {
    background-color: #f0f0f0;
    transform: translateY(1px);
  }

  svg {
    margin-right: 2px;
    stroke: #8e8e8e;
  }

  @media (max-width: 576px) {
    padding: 8px 12px;
    font-size: 0.8rem;
    flex: 1;
    justify-content: center;

    &:first-child {
      span.full-text {
        display: none;
      }

      span.short-text {
        display: inline;
      }
    }
  }

  span.short-text {
    display: none;
  }
`;

const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ListIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

export default LNotice;
